// The shapes the simulation moves around.

import type { Rng } from "./rng";
import type { WorldMap } from "./map";

/** 0 down, 1 up, 2 left, 3 right. Four directions, like the game this is a
 *  parody of. */
export type Dir = 0 | 1 | 2 | 3;

export const DIR_VECTORS: Record<Dir, readonly [number, number]> = {
  0: [0, 1],
  1: [0, -1],
  2: [-1, 0],
  3: [1, 0],
};

/** Which of the two places a body can be. Nothing may see, hit or be told
 *  about anything in the other one. */
export type Zone = 0 | 1;

export interface Player {
  id: number;
  accountId: string;
  characterId: string;
  /** Always "player". Carried rather than assumed so the renderer has one
   *  place to read a nameplate from, whoever the body belongs to. */
  name: string;
  /** The one thing that is not the same for everybody. */
  slug: string;
  x: number;
  y: number;
  /** The lane currently being crossed. It can differ from `facing` until the
   *  body reaches the next tile centre and takes a queued turn. */
  dir: Dir;
  /** The last direction the player asked for. The body and sword face it
   *  immediately, even when movement must finish the current lane first. */
  facing: Dir;
  hp: number;
  gold: number;
  /** Movement intent, refreshed by the newest input message. */
  mx: number;
  my: number;
  /** A non-zero intent stays pending until movement has read it at a tile
   *  centre. A stop received before then is applied just after that read. */
  intentPending: boolean;
  stopPending: boolean;
  /** Seconds until the sword can swing again, and how much of the current
   *  swing is left. A swing is live while `swing > 0`. */
  cooldown: number;
  swing: number;
  /** Bodies already cut by the current swing, so one press is one hit each. */
  struck: Set<number>;
  /** Seconds of invulnerability left after being hurt. */
  invuln: number;
  /** How long this body has been standing perfectly still, which is the only
   *  thing that mends it outside the shop. */
  still: number;
  regen: number;
  /** Seconds until a dead body walks back into the world. Undefined when
   *  alive. */
  respawn?: number;
  /** Seconds since the socket driving this body went away, or undefined while
   *  somebody is holding it. */
  linkdead?: number;
}

export interface Enemy {
  id: number;
  x: number;
  y: number;
  dir: Dir;
  hp: number;
  /** Where it was spawned. It wanders around this and is pulled back to it. */
  homeX: number;
  homeY: number;
  /** The player it is chasing, if any. */
  target?: number;
  /** Seconds until it makes its next idle decision, and what that decision
   *  was: a heading to stroll along, or undefined for "stand there". */
  think: number;
  wander?: Dir;
  /** Per-victim touch cooldowns, so an ooze sitting on you does not drain you
   *  at the tick rate. */
  touched: Map<number, number>;
  /** Seconds of hurt flash left, purely so the client can flicker it. */
  flash: number;
}

/** A dead ooze's slot in the field, counting down to its replacement. */
export interface Respawn {
  x: number;
  y: number;
  seconds: number;
}

export interface ChatLine {
  id: number;
  from: string;
  text: string;
  kind: "say" | "system";
}

/** Something that happened this tick and is worth a spark on the client.
 *
 *  The numbers are the wire format and the names are how anything reads or
 *  writes one. They were bare integers in three places at once — emitted here,
 *  given a lifetime and a colour on the client, and explained in a comment —
 *  which is three tables that had to agree with nothing keeping them honest. */
export const EVENT = {
  swordHit: 0,
  enemyDied: 1,
  playerHurt: 2,
  playerDied: 3,
  potionDrunk: 4,
} as const;

export type EventKind = (typeof EVENT)[keyof typeof EVENT];

export interface WorldEvent {
  kind: EventKind;
  x: number;
  y: number;
}

export interface World {
  map: WorldMap;
  rng: Rng;
  tick: number;
  time: number;
  players: Map<number, Player>;
  enemies: Map<number, Enemy>;
  respawns: Respawn[];
  chat: ChatLine[];
  /** System lines produced this tick, drained by the Durable Object so they
   *  can be persisted. */
  notices: string[];
  events: WorldEvent[];
  nextId: number;
  nextChatId: number;
}
